import React, { useEffect, useState } from "react";
import "../styles/pagos.css";
import CargoAnularModal from "./CargoAnularModal";

const currency = new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" });

const EstadoMatriculaChip = ({ estado }) => {
  if (!estado) return null;
  const color = estado.toLowerCase() === "activa" ? "success" : "secondary";
  return <span className={`badge bg-${color} text-capitalize`} style={{ borderRadius: "999px", padding: "0.3em 0.75em" }}>{estado}</span>;
};

export default function DeudaDetailPanel({ deuda, onClose, onCobrarSeleccion, cargos = [], onAnularCargo, onNuevoCargo }) {
  const [cargoAAnular, setCargoAAnular] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());

  useEffect(() => {
    setSeleccionados(new Set());
  }, [deuda?.id, cargos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmarAnular = async () => {
    if (!cargoAAnular) return;
    setAnulando(true);
    try { await onAnularCargo(cargoAAnular.id); }
    finally { setAnulando(false); setCargoAAnular(null); }
  };

  if (!deuda) {
    return (
      <div className="management-card pago-detail empty h-100">
        <p className="text-muted mb-1">Selecciona una matricula para ver el detalle.</p>
        <small className="text-muted">Tip: usa la tabla de cobranzas para escoger.</small>
      </div>
    );
  }

  const total = deuda.totalAPagar || 0;
  const saldo = deuda.saldo || 0;
  const pagado = deuda.pagado || Math.max(total - saldo, 0);

  const toggleItem = (key) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allKeys = [...(saldo > 0 ? ["matricula"] : []), ...cargos.map((c) => c.id)];
  const todosSeleccionados = allKeys.length > 0 && allKeys.every((k) => seleccionados.has(k));
  const toggleAll = () => setSeleccionados(todosSeleccionados ? new Set() : new Set(allKeys));

  const montoMatricula = seleccionados.has("matricula") ? saldo : 0;
  const cargosSeleccionados = cargos.filter((c) => seleccionados.has(c.id));
  const montoCargos = cargosSeleccionados.reduce((sum, c) => sum + Number(c.monto || 0), 0);
  const montoSeleccionado = montoMatricula + montoCargos;
  const algunoSeleccionado = seleccionados.size > 0 && montoSeleccionado > 0;
  const includeMatricula = seleccionados.has("matricula") && saldo > 0;

  const handleCobrar = () => {
    if (!algunoSeleccionado || !onCobrarSeleccion) return;
    const seleccion_items = [
      ...(includeMatricula ? [{
        key: `mat-${deuda.matriculaId}`,
        type: "matricula",
        matricula_id: deuda.matriculaId,
        label: "Saldo de matrícula",
        monto: montoMatricula,
        concepto_id: null,
        cargo_id: null,
      }] : []),
      ...cargosSeleccionados.map((c) => ({
        key: `cargo-${c.id}`,
        type: "cargo",
        matricula_id: deuda.matriculaId,
        label: c.concepto_nombre + (c.observaciones ? ` — ${c.observaciones}` : ""),
        monto: Number(c.monto),
        concepto_id: c.concepto_id,
        cargo_id: c.id,
      })),
    ];
    onCobrarSeleccion({
      monto: includeMatricula ? montoMatricula : montoCargos,
      monto_total_cobro: montoSeleccionado,
      cargo_ids: cargosSeleccionados.map((c) => c.id),
      primer_concepto_id: cargosSeleccionados[0]?.concepto_id || null,
      includeMatricula,
      seleccion_items,
    });
  };

  return (
    <div className="management-card pago-detail deuda-detail h-100 d-flex flex-column">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="management-eyebrow mb-1">Detalle de cobranza</p>
          <h6 className="mb-1 fw-bold">{deuda.estudiante || "Estudiante no disponible"}</h6>
          <div className="text-muted small d-flex align-items-center gap-2 mb-1">
            <span>Matricula: <strong className="text-dark">{deuda.codigoMatricula || `#${deuda.matriculaId}`}</strong></span>
            <EstadoMatriculaChip estado={deuda.estadoMatricula} />
          </div>
          <div className="text-muted small">Promocion: <strong className="text-dark">{deuda.promocion || "—"}</strong></div>
          <div className="text-muted small">Curso: <strong className="text-dark">{deuda.curso || "—"}</strong></div>
        </div>
        <button type="button" className="btn btn-link text-decoration-none text-muted p-0" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {/* Totales */}
      <div className="d-flex flex-column gap-1 mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted small">Total a pagar</span>
          <strong>{currency.format(total)}</strong>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted small">Pagado</span>
          <strong className="text-success">{currency.format(pagado)}</strong>
        </div>
      </div>

      {/* Cobros pendientes con checkboxes */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="management-eyebrow mb-0">Cobros pendientes</p>
          <button
            type="button"
            className="btn btn-sm"
            style={{ borderRadius: "999px", fontSize: "0.72rem", border: "1px solid #ffc107", color: "#856404", padding: "0.2rem 0.65rem", minHeight: 36 }}
            onClick={onNuevoCargo}
          >
            <i className="bi bi-box me-1" />
            Agregar cargo
          </button>
        </div>

        <div className="d-flex flex-column gap-2">
          {saldo > 0 && (
            <label className="deuda-item-row deuda-item-row--matricula">
              <input
                type="checkbox"
                className="form-check-input flex-shrink-0 mt-0"
                checked={seleccionados.has("matricula")}
                onChange={() => toggleItem("matricula")}
              />
              <div className="flex-fill ms-2" style={{ minWidth: 0 }}>
                <div className="fw-semibold small">Saldo de matrícula</div>
              </div>
              <strong className="small flex-shrink-0">{currency.format(saldo)}</strong>
            </label>
          )}

          {cargos.map((cargo) => (
            <label key={cargo.id} className="deuda-item-row deuda-item-row--cargo">
              <input
                type="checkbox"
                className="form-check-input flex-shrink-0 mt-0"
                checked={seleccionados.has(cargo.id)}
                onChange={() => toggleItem(cargo.id)}
              />
              <div className="flex-fill ms-2" style={{ minWidth: 0 }}>
                <div className="fw-semibold small text-truncate">{cargo.concepto_nombre}</div>
                {cargo.observaciones && <small className="text-muted d-block text-truncate">{cargo.observaciones}</small>}
              </div>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <strong className="small">{currency.format(cargo.monto)}</strong>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  title="Anular cargo"
                  style={{ minWidth: 36, minHeight: 36 }}
                  onClick={(e) => { e.preventDefault(); setCargoAAnular(cargo); }}
                >
                  <i className="bi bi-trash" />
                </button>
              </div>
            </label>
          ))}

          {saldo === 0 && cargos.length === 0 && (
            <p className="text-muted small text-center py-2 mb-0">Sin cobros pendientes</p>
          )}
        </div>

        {allKeys.length > 1 && (
          <div className="deuda-item-row deuda-item-row--total mt-2">
            <label className="d-flex align-items-center gap-2 small fw-semibold mb-0 flex-fill" style={{ cursor: "pointer" }}>
              <input type="checkbox" className="form-check-input mt-0" checked={todosSeleccionados} onChange={toggleAll} />
              Seleccionar todo
            </label>
            <span className="deuda-seleccionado-badge">{currency.format(montoSeleccionado)}</span>
          </div>
        )}
      </div>

      {/* Botón cobrar */}
      <div>
        <button
          type="button"
          className="btn management-btn-primary w-100"
          disabled={!algunoSeleccionado}
          onClick={handleCobrar}
        >
          <i className="bi bi-cash-stack me-2" />
          {algunoSeleccionado ? `Cobrar ${currency.format(montoSeleccionado)}` : "Cobrar selección"}
        </button>
      </div>

      <CargoAnularModal
        open={!!cargoAAnular}
        cargo={cargoAAnular}
        deleting={anulando}
        onClose={() => setCargoAAnular(null)}
        onConfirm={handleConfirmarAnular}
      />
    </div>
  );
}
